import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { updateObjective } from '../../store/map3PendingSlice';
import { Objective1, Status1 } from '../../types';
import { upsertItem } from '../../utils/upsertItem';
import { useResultsAndComments } from '../../hooks/useIntervalData';
import { ResultItem3 } from '../dev/ResultItem3';
import { AddResultButton1 } from '../dev/AddResultButton1';
import { CommentBubble1 } from './CommentBubble1';
import { AddEditComment1 } from './AddEditComment1';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';
import { isMetricObjective, calculateContinuousCode } from '../../utils/kpiUtils';
import { getBackgroundColorClass } from '../../utils/colorUtils';
import { useCurrentMap } from '../../hooks/useCurrentMap';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '../../utils/getPermissionsForRole';
import { Button } from '../../components_catalyst/button';
import { Divider } from '../../components_catalyst/divider';
// mr-stiv: use drawer-local Listbox variant that renders options inline
// (no HUI portal) so clicks stay inside Map3Drawer's panelRef and do not
// trigger the capture-phase pointerdown handler's outside-click close path.
// See ./listbox-in-dialog.tsx for the full rationale.
import { Listbox, ListboxLabel, ListboxOption } from './listbox-in-dialog';
import { getIntervalsForMilestoneMap } from '../../utils/getIntervalsForMilestoneMap';
import { formatIntervalLabel } from '../../utils/intervalUtils';
import type { IntervalOption } from '../../utils/intervalUtils';

/**
 * StatusTab1 — Drawer tab for status editing.
 *
 * Accessed via route: objectives/:objectiveShortId/status?forInterval=<forIntervalEnding>
 * Mirrors the body content of StatusTooltip1 in drawer form.
 *
 * Sections:
 *  1. Status header (interval date display)
 *  2. Status buttons (deliverable) OR metric input (metric)
 *  3. Divider (soft)
 *  4. Results list (ResultItem3 + Add Result button)
 *  5. Divider (soft)
 *  6. Interval-scoped comments (CommentBubble1 list + AddEditComment1)
 *
 * Comments were previously in the separate StatusCommentsTab (route: status-comments),
 * which has since been removed. They are now inlined here below the results section.
 */
export function StatusTab1() {
  const [searchParams, setSearchParams] = useSearchParams();
  const objectiveShortId = searchParams.get('objectiveId') ?? undefined;
  const forIntervalEnding = searchParams.get('forInterval') || '';

  // Get milestoneMapUuid from currentMap hook (was previously from outlet context)
  const currentMap = useCurrentMap();
  const milestoneMapUuid = currentMap?.uuid ?? '';

  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj: Objective1) => obj.shortId === objectiveShortId),
  );

  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canEdit } = getPermissionsForRole(userRole);

  // Build interval options for the "Status as of" dropdown
  const intervalOptions: IntervalOption[] = useMemo(() => {
    if (!currentMap) return [];
    const intervals = getIntervalsForMilestoneMap(currentMap);
    return intervals.map((interval) => ({
      label: formatIntervalLabel(interval, currentMap.interval),
      interval,
    }));
  }, [currentMap]);

  // Determine selected interval option from ?forInterval param
  const selectedOption: IntervalOption | null = useMemo(() => {
    if (!forIntervalEnding || intervalOptions.length === 0) {
      // Default to the most recent interval when none is selected
      return intervalOptions.length > 0 ? intervalOptions[intervalOptions.length - 1] : null;
    }
    // Match by comparing the interval's end date ISO string to forIntervalEnding
    const match = intervalOptions.find(
      (opt) => opt.interval !== null && opt.interval.end.toISOString() === forIntervalEnding,
    );
    return match ?? (intervalOptions.length > 0 ? intervalOptions[intervalOptions.length - 1] : null);
  }, [forIntervalEnding, intervalOptions]);

  /** Write the selected interval's end-date ISO string to ?forInterval in the URL */
  const handleIntervalChange = (option: IntervalOption) => {
    if (option.interval === null) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.set('forInterval', option.interval.end.toISOString());
    setSearchParams(newParams, { replace: true });
  };

  if (!objective) {
    return <div className="px-4 py-4 text-sm text-gray-500">Objective not found.</div>;
  }

  // The effective interval ending to use — either from URL or from selected option
  const effectiveIntervalEnding = forIntervalEnding || selectedOption?.interval?.end.toISOString() || '';

  // Find the status for this interval (by forIntervalEnding match)
  const status = objective.statuses.find(
    (s: Status1) => s.forIntervalEnding === effectiveIntervalEnding,
  );

  return (
    <div className="overflow-hidden">
      {/* Status as of dropdown */}
      {intervalOptions.length > 0 && (
        <div className="px-4 pt-3 pb-2 flex items-center gap-2">
          <span className="text-sm text-gray-600 whitespace-nowrap">Status as of:</span>
          <Listbox
            value={selectedOption}
            onChange={handleIntervalChange}
            aria-label="Status as of"
          >
            {intervalOptions.map((option, idx) => (
              <ListboxOption key={idx} value={option}>
                <ListboxLabel>{option.label}</ListboxLabel>
              </ListboxOption>
            ))}
          </Listbox>
        </div>
      )}

      {/* No interval fallback (when map has no intervals yet) */}
      {intervalOptions.length === 0 && (
        <div className="px-4 py-4 text-sm text-gray-500">
          No interval selected. Click a status cell to open this tab.
        </div>
      )}

      {/* Status input section */}
      {effectiveIntervalEnding && (
        <>
          <TabStatus
            forIntervalEnding={effectiveIntervalEnding}
            statusUuid={status?.uuid || ''}
            objectiveUuid={objective.uuid}
            milestoneMapUuid={milestoneMapUuid}
            canEdit={canEdit}
          />

          {/* Divider between status buttons and results */}
          <div className="my-4 px-4">
            <Divider soft />
          </div>

          {/* Results section */}
          <TabResults
            forIntervalEnding={effectiveIntervalEnding}
            statusUuid={status?.uuid || ''}
            objectiveUuid={objective.uuid}
            milestoneMapUuid={milestoneMapUuid}
          />

          {/* Divider between results and comments */}
          <div className="my-4 px-4">
            <Divider soft />
          </div>

          {/* Interval-scoped comments */}
          <TabComments
            forIntervalEnding={effectiveIntervalEnding}
            objectiveUuid={objective.uuid}
            milestoneMapUuid={milestoneMapUuid}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components (mirror TooltipStatus, TooltipResults, TooltipComments)
// ---------------------------------------------------------------------------

function TabStatus({
  forIntervalEnding,
  statusUuid,
  objectiveUuid,
  milestoneMapUuid,
  canEdit,
}: {
  forIntervalEnding: string;
  statusUuid: string;
  objectiveUuid: string;
  milestoneMapUuid: string;
  canEdit: boolean;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const { results } = useResultsAndComments(forIntervalEnding, objectiveUuid, milestoneMapUuid);
  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj: Objective1) => obj.uuid === objectiveUuid),
  );

  if (!objective?.uuid) {
    return <div className="px-4 py-4 text-sm">Loading...</div>;
  }

  const status = objective?.statuses.find((s: Status1) => s.uuid === statusUuid);
  const hasResults = results.length > 0;
  const hasKeyResults = results.some((result) => result.isKeyResult);

  const handleStatusUpdate = (statusCode: '?' | 'p' | 'g' | 'y' | 'r') => {
    if (!canEdit) {
      console.warn('Status update denied: user does not have edit permissions');
      return;
    }

    const now = new Date().toISOString();
    const uuid = crypto.randomUUID();

    const newStatus = {
      uuid: status?.uuid || uuid,
      shortId: status?.shortId || getShortIdFromUuid(uuid),
      forIntervalEnding,
      createdDate: status?.createdDate || now,
      lastUpdatedDate: now,
      statusCode,
    };

    const existingStatuses = objective?.statuses || [];
    const updatedStatuses = upsertItem(
      existingStatuses,
      newStatus,
      (item) => item.uuid === statusUuid,
    );

    dispatch(
      updateObjective({
        uuid: objective.uuid,
        updates: {
          statuses: updatedStatuses,
        },
      }),
    );
  };

  // Description sets (same as StatusTooltip1)
  const defaultDescriptions = {
    '?': 'To be determined, work may happen here',
    p: 'Work is planned/expected here',
    g: 'On track to hit next inchstone or milestone',
    y: 'Unable to hit next inchstone, but able to hit the next milestone',
    r: 'Next milestone cannot be hit without adjusting timeline, scope, or resources',
  };

  const resultsOnlyDescriptions = {
    '?': 'TBD (This status is not applicable here)',
    p: 'Work is planned/expected here',
    g: 'Inchstone hit (checked results affirm work is on track to hit next milestone)',
    y: 'Inchstone not hit (targeted results unchecked, but the next milestone is still achievable)',
    r: 'Next milestone cannot be hit without adjusting timeline, scope, or resources',
  };

  const keyResultsDescriptions = {
    '?': 'TBD (This status is not applicable here)',
    p: 'Work is planned/expected here',
    g: 'Milestone hit, key results delivered!',
    y: 'Milestone partially hit, key results partially delivered',
    r: 'Milestone not hit, key results not delivered',
  };

  const descriptions = hasKeyResults
    ? keyResultsDescriptions
    : hasResults && !hasKeyResults
      ? resultsOnlyDescriptions
      : defaultDescriptions;

  const currentStatusCode = status?.statusCode || '';

  const isMetric = isMetricObjective(objective.type);

  // Metric input mode
  if (isMetric && objective.kpiSettings) {
    return (
      <TabMetricInput
        objective={objective}
        status={status}
        forIntervalEnding={forIntervalEnding}
        canEdit={canEdit}
      />
    );
  }

  // Discrete status buttons (deliverable)
  return (
    <div className="text-sm">
      <div className="px-4 py-4">
        <div className="flex flex-col space-y-2">
          <TabStatusButton
            statusCode="?"
            bgClass="bg-status-tbd"
            onClick={() => handleStatusUpdate('?')}
            description={descriptions['?']}
            isSelected={currentStatusCode === '?'}
          />
          <TabStatusButton
            statusCode="p"
            bgClass="bg-status-p"
            onClick={() => handleStatusUpdate('p')}
            description={descriptions['p']}
            isSelected={currentStatusCode === 'p'}
          />
          <TabStatusButton
            statusCode="g"
            bgClass="bg-status-g"
            onClick={() => handleStatusUpdate('g')}
            description={descriptions['g']}
            isSelected={currentStatusCode === 'g'}
          />
          <TabStatusButton
            statusCode="y"
            bgClass="bg-status-y"
            onClick={() => handleStatusUpdate('y')}
            description={descriptions['y']}
            isSelected={currentStatusCode === 'y'}
          />
          <TabStatusButton
            statusCode="r"
            bgClass="bg-status-r"
            onClick={() => handleStatusUpdate('r')}
            description={descriptions['r']}
            isSelected={currentStatusCode === 'r'}
          />
        </div>
      </div>
    </div>
  );
}

function TabStatusButton({
  statusCode,
  bgClass,
  onClick,
  description,
  isSelected,
}: {
  statusCode: string;
  bgClass: string;
  onClick: () => void;
  description: string;
  isSelected: boolean;
}) {
  const showStatusCodes = useSelector(
    (state: RootState) => state.map3UI.mapControls.showStatusCodes,
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={`flex items-center space-x-3 p-1 rounded-md cursor-pointer group transition-all duration-150
        ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500' : 'hover:bg-gray-50 dark:hover:bg-gray-700/20'}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`Set status to ${statusCode}: ${description}`}
      aria-pressed={isSelected}
    >
      <div
        className={`w-8 h-8 ${bgClass} rounded-md flex items-center justify-center flex-shrink-0
          ${
            isSelected
              ? 'ring-1 ring-blue-500 shadow-lg'
              : 'group-hover:outline group-hover:outline-2 group-hover:outline-blue-500 group-focus:outline group-focus:outline-2 group-focus:outline-blue-500'
          }`}
      >
        <span className="text-center">{showStatusCodes ? statusCode : ''}</span>
      </div>
      <span className={`text-sm flex-1 ${isSelected ? 'font-medium' : ''}`}>{description}</span>
    </div>
  );
}

function TabMetricInput({
  objective,
  status,
  forIntervalEnding,
  canEdit,
}: {
  objective: Objective1;
  status: Status1 | undefined;
  forIntervalEnding: string;
  canEdit: boolean;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const [inputValue, setInputValue] = useState<number>(status?.value || 0);

  const kpiSettings = objective.kpiSettings!;
  const { ranges, higherIsBetter, numberType } = kpiSettings;

  const min = higherIsBetter ? ranges.worstRed : ranges.bestGreen;
  const max = higherIsBetter ? ranges.bestGreen : ranges.worstRed;

  const continuousCode = calculateContinuousCode(inputValue, kpiSettings);
  const bgColorClass = getBackgroundColorClass(continuousCode);

  const handleValueChange = (newValue: number) => {
    setInputValue(newValue);
  };

  const handleSave = () => {
    if (!canEdit) {
      console.warn('Status update denied: user does not have edit permissions');
      return;
    }

    const now = new Date().toISOString();
    const uuid = crypto.randomUUID();

    const newStatus: Status1 = {
      uuid: status?.uuid || uuid,
      shortId: status?.shortId || getShortIdFromUuid(uuid),
      forIntervalEnding,
      createdDate: status?.createdDate || now,
      lastUpdatedDate: now,
      statusCode: continuousCode,
      value: inputValue,
    };

    const updatedStatuses = upsertItem(
      objective?.statuses || [],
      newStatus,
      (item) => item.uuid === (status?.uuid || uuid),
    );

    dispatch(
      updateObjective({
        uuid: objective.uuid,
        updates: {
          statuses: updatedStatuses,
        },
      }),
    );
  };

  return (
    <div className="text-sm px-4 py-4 space-y-4">
      {/* Current Value Display */}
      <div className="text-center">
        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {inputValue}
          {numberType === '%' ? '%' : ''}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Status Code: <span className="font-mono">{continuousCode}</span>
        </div>
      </div>

      {/* Color Preview */}
      <div className={`h-12 rounded-md ${bgColorClass} flex items-center justify-center`}>
        <span className="text-sm font-medium">Current Performance Level</span>
      </div>

      {/* Range Slider */}
      <div className="space-y-2">
        <input
          type="range"
          min={min}
          max={max}
          step={numberType === '%' ? 1 : 0.1}
          value={inputValue}
          onChange={(e) => handleValueChange(Number(e.target.value))}
          disabled={!canEdit}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 disabled:opacity-50"
        />
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>
            {min}
            {numberType === '%' ? '%' : ''}
          </span>
          <span>
            {max}
            {numberType === '%' ? '%' : ''}
          </span>
        </div>
      </div>

      {/* Visual Color Guide */}
      <div className="space-y-1">
        <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
          Performance Zones:
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="space-y-1">
            <div className="bg-status-r1 h-6 rounded flex items-center justify-center">
              <span className="text-white font-medium">Red</span>
            </div>
            <div className="text-center text-gray-600 dark:text-gray-400">
              {higherIsBetter ? `< ${ranges.worstYellow}` : `> ${ranges.worstYellow}`}
              {numberType === '%' ? '%' : ''}
            </div>
          </div>
          <div className="space-y-1">
            <div className="bg-status-y7 h-6 rounded flex items-center justify-center">
              <span className="font-medium">Yellow</span>
            </div>
            <div className="text-center text-gray-600 dark:text-gray-400">
              {higherIsBetter
                ? `${ranges.worstYellow}-${ranges.bestYellow}`
                : `${ranges.bestYellow}-${ranges.worstYellow}`}
              {numberType === '%' ? '%' : ''}
            </div>
          </div>
          <div className="space-y-1">
            <div className="bg-status-g22 h-6 rounded flex items-center justify-center">
              <span className="text-white font-medium">Green</span>
            </div>
            <div className="text-center text-gray-600 dark:text-gray-400">
              {higherIsBetter ? `> ${ranges.bestYellow}` : `< ${ranges.bestYellow}`}
              {numberType === '%' ? '%' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      {canEdit && (
        <div className="pt-2">
          <Button color="indigo" className="w-full" onClick={handleSave}>
            Save Metric Value
          </Button>
        </div>
      )}
    </div>
  );
}

function TabResults({
  forIntervalEnding,
  statusUuid,
  objectiveUuid,
  milestoneMapUuid,
}: {
  forIntervalEnding: string;
  statusUuid: string;
  objectiveUuid: string;
  milestoneMapUuid: string;
}) {
  const { results } = useResultsAndComments(forIntervalEnding, objectiveUuid, milestoneMapUuid);

  return (
    <div className="px-4 py-4">
      {results.map((result) => (
        <div
          key={result.uuid}
          className="flex items-start space-x-2 relative rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 p-1"
        >
          <ResultItem3
            resultUuid={result.uuid}
            objectiveUuid={objectiveUuid}
            deadline={result.deadline!}
          />
        </div>
      ))}
      <AddResultButton1 objectiveUuid={objectiveUuid} latestIntervalEnding={forIntervalEnding} />
    </div>
  );
}

function TabComments({
  forIntervalEnding,
  objectiveUuid,
  milestoneMapUuid,
}: {
  forIntervalEnding: string;
  objectiveUuid: string;
  milestoneMapUuid: string;
}) {
  const { comments } = useResultsAndComments(forIntervalEnding, objectiveUuid, milestoneMapUuid);

  return (
    <section aria-label="Comments for this status period" className="px-4 py-4">
      {comments.length > 0 && (
        <ul className="-mt-10 mb-6" role="list" aria-label="Comments for this interval">
          {comments.map((comment, commentIdx) => (
            <li key={comment.uuid}>
              <CommentBubble1
                commentUuid={comment.uuid}
                showTopBorder={commentIdx !== 0}
                parentObjectiveUuid={objectiveUuid}
              />
            </li>
          ))}
        </ul>
      )}

      <AddEditComment1 objectiveUuid={objectiveUuid} forIntervalEnding={forIntervalEnding} />
    </section>
  );
}

